import React from 'react';
import useTranslation from 'next-translate/useTranslation';

import theme from '@styles/theme';
import { css } from '@emotion/react';
import {
  Coin1Icon,
  StorygameSaleIconMobile,
  StorygameTimeIconMobile,
  StorygameAutorenewIconMobile,
  StorygameHourglassIconMobile,
  NavCloseBtn,
} from 'public/assets';

import { insertCommas } from '@/common/utils';
import { CoinProductFragment } from '@/operations/queries/story/__generated__/useGetStoryGameDetail.generated';
import SPImage from '../../image/SPImage';

interface IMobilePopupContainer {
  storyName?: string;
  originalCoin?: number;
  storyCoin: number;
  onClose: () => void;
  coverImage?: string;
  discountRate: number;
  coinProduct?: CoinProductFragment | null;
  onPurchase: () => void;
}

interface IStoryGameBenefitItem {
  icon: 'sale' | 'time' | 'autoRenew' | 'hourGlass';
  text: string;
  delay: number;
}

const StoryGameBenefitItem = ({ icon, text, delay }: IStoryGameBenefitItem) => {
  return (
    <div css={benefitItemWrapper({ delay })}>
      <div className="icon-wrapper">
        {icon === 'sale' ? (
          <StorygameSaleIconMobile className="icon" width={16} height={17} />
        ) : icon === 'time' ? (
          <StorygameTimeIconMobile className="icon" />
        ) : icon === 'autoRenew' ? (
          <StorygameAutorenewIconMobile className="icon" />
        ) : (
          <StorygameHourglassIconMobile className="icon" />
        )}
      </div>
      <div className="benefit-text">
        <p className="bottom-text">{text}</p>
      </div>
    </div>
  );
};

const StoryGameMobilePopupContainer = ({
  storyName,
  storyCoin,
  originalCoin,
  onClose,
  coverImage,
  discountRate,
  coinProduct,
  onPurchase,
}: IMobilePopupContainer) => {
  const { t } = useTranslation();

  return (
    <div css={mobileContainer}>
      <header className="header">
        <NavCloseBtn
          width={22}
          height={22}
          fill={theme.colors.white}
          className="back"
          onClick={onClose}
        />
      </header>
      <div className="cover">
        <div className="image-wrapper">
          {coverImage && (
            <SPImage src={coverImage} objectFit="cover" layout="fill" />
          )}
        </div>
        <div className="title-wrapper">
          <p className="story-name">{storyName}</p>
          <h1 className="title">
            {t('store_screen_label_benefit_unlimited_pass')}
          </h1>
          <p className="description">
            {t('storydetail_popup_label_purchase_now_benefit', {
              storyName,
            })}
          </p>
        </div>
      </div>
      <div className="benefit-list">
        <StoryGameBenefitItem
          icon="sale"
          text={t('store_screen_title_benefit_one', {
            value: coinProduct
              ? (coinProduct.price /
                  (coinProduct.originalPrice || coinProduct.price)) *
                100
              : discountRate,
          })}
          delay={0.2}
        />
        <StoryGameBenefitItem
          icon="time"
          text={t('store_screen_title_benefit_two')}
          delay={0.4}
        />
        <StoryGameBenefitItem
          icon="autoRenew"
          text={t('store_screen_title_benefit_three')}
          delay={0.6}
        />
        <StoryGameBenefitItem
          icon="hourGlass"
          text={t('store_screen_title_benefit_four')}
          delay={0.8}
        />
      </div>
      <div className="dark-layer"></div>
      <div className="button-wrapper">
        <div className="price">
          <div>Total</div>
          <div className="price-right-wrapper">
            {coinProduct ? (
              <>
                {coinProduct.price /
                  (coinProduct.originalPrice || coinProduct.price) !==
                  1 && (
                  <>
                    <div className="discount-percent">
                      -
                      {(coinProduct.price /
                        (coinProduct.originalPrice || coinProduct.price)) *
                        100}
                      %
                    </div>
                    <div className="coin-wrapper origin">
                      <p className="coin-number">
                        {insertCommas(coinProduct.originalPrice)}
                      </p>
                    </div>
                  </>
                )}
                <div className="coin-wrapper discount">
                  <p className="coin-number">
                    {t('payment_screen_description_price_monetary_unit', {
                      value: insertCommas(coinProduct.price),
                    })}
                  </p>
                </div>
              </>
            ) : (
              <>
                {storyCoin / (originalCoin || storyCoin) !== 1 && (
                  <>
                    <div className="discount-percent">
                      -{(storyCoin / (originalCoin || storyCoin)) * 100}%
                    </div>
                    <div className="coin-wrapper origin">
                      <Coin1Icon width={24} height={24} />
                      <p className="coin-number">{originalCoin}</p>
                    </div>
                  </>
                )}
                <div className="coin-wrapper discount">
                  <Coin1Icon width={24} height={24} />
                  <p className="coin-number">{storyCoin}</p>
                </div>
              </>
            )}
          </div>
        </div>
        <button className="whole-purchase-button" onClick={onPurchase}>
          <p className="whole-purchase-button-text">
            {t('store_popup_button_buy_unlimited_story')}
          </p>
        </button>
      </div>
    </div>
  );
};

const mobileContainer = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  color: ${theme.colors.white};
  align-items: center;
  height: 100%;
  overflow: hidden;
  background: black;
  padding: 0 1.5rem;

  .header {
    height: 3rem;
    position: fixed;
    display: flex;
    align-items: center;
    color: #fff;
  }

  .cover {
    width: 100%;
    padding-bottom: 0.75rem;
    padding-top: 0.625rem;

    .image-wrapper {
      position: relative;
      width: 100%;
      padding-bottom: 70%;
      border-radius: 0.5rem;

      img {
        border-radius: 0.5rem;
      }
    }

    .title-wrapper {
      margin-top: 12px;
      width: 100%;
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      gap: 0.5em;
      backdrop-filter: blur(8px);

      .story-name {
        color: #9457eb;
        ${theme.fontV3.body.body04Reg}
      }

      .title {
        color: #fff;
        ${theme.fontV3.body.body02Medium}
      }

      .description {
        color: rgba(255, 255, 255, 0.5);
        ${theme.fontV3.body.body06Reg}
      }
    }
  }

  .benefit-list {
    width: 100%;
    align-self: flex-start;
    flex-direction: column;
    display: flex;
    padding-bottom: 8.5rem;
    overflow-y: scroll;
    backdrop-filter: blur(8px);
    height: 15.625rem;

    ::-webkit-scrollbar {
      display: none;
    }
  }

  .dark-layer {
    position: fixed;
    bottom: env(safe-area-inset-bottom);
    width: 100vw;
    height: 8.5rem;
    flex-direction: column;
    align-items: center;
    background: linear-gradient(0deg, #000 75%, rgba(0, 0, 0, 0) 100%);
    z-index: 1;
  }

  .button-wrapper {
    width: 100%;
    padding: 0 1.5rem;
    position: fixed;
    bottom: env(safe-area-inset-bottom);
    z-index: 1;
    display: flex;
    margin: 1.25em 1em;
    flex-direction: column;

    .price {
      display: flex;
      justify-content: space-between;
      color: rgba(255, 255, 255, 0.5);
      margin-bottom: 16px;
      ${theme.fontV3.body.body04Reg}

      .price-right-wrapper {
        display: flex;
        align-items: center;

        .discount-percent {
          width: 100%;
          height: 1.0625rem;
          padding: 0.125rem 0.25rem;
          border-radius: 0.125rem;
          background: #ff3a58;
          color: #fff;
          text-align: center;
          ${theme.fontV3.body.caption02Reg}
        }

        .coin-wrapper {
          display: flex;
          justify-content: center;
          align-center: center;
          margin-left: 0.375rem;

          .coin-number {
            padding-top: 2px;
            color: white;
          }
        }

        .coin-wrapper.origin {
          opacity: 0.5;
          text-decoration: line-through;
        }

        .coin-wrapper.discount {
          flex-shrink: 0;
        }
      }
    }

    .whole-purchase-button {
      width: 100%;
      height: 3rem;
      border-radius: 0.375em;
      cursor: pointer;
      background-color: ${theme.colors.primary500};
      color: ${theme.colors.white};
      border: 0.0625em solid ${theme.colors.primary500};
      ${theme.fontV3.body.body03Reg}

      .whole-purchase-button-text {
        display: flex;
        justify-content: center;
        gap: 0.25em;
      }
    }
  }
`;

const benefitItemWrapper = ({ delay }: { delay: number }) => css`
  display: flex;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  animation: up ${delay}s ease-out;
  align-items: center;
  margin-bottom: 0.375rem;
  border-radius: 0.375rem;
  background: rgba(255, 255, 255, 0.05);

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:last-child {
    margin-bottom: 0;
  }

  .icon {
    flex-shrink: 0;
  }

  .benefit-text {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    color: rgba(255, 255, 255, 0.75);
    padding-top: 0.0625rem;

    .text {
      color: rgba(255, 255, 255, 0.75);
      ${theme.fontV3.body.body04Reg}
    }
  }

  @keyframes up {
    0% {
      transform: translateY(200px);
    }

    100% {
      transform: translateY(0);
    }
  }
`;

export default StoryGameMobilePopupContainer;
